import React, { useEffect, useMemo, useRef, useState } from 'react'
import { format } from 'date-fns'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Button from '@/v2/components/common/Button'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import {
  useUnconfirmedAppointments,
  useSetAppointmentConfirmed,
  useCancelAppointment,
} from '@/v2/hooks/appointments/useUnconfirmedAppointments'
import { nowOnClinicTimezone } from '@/v2/utils/convert'
import Filters from './components/Filters'
import AppointmentRow from './components/AppointmentRow'
import MessagesDrawer from './components/MessagesDrawer'
import CancelAppointmentModal from './components/CancelAppointmentModal'
import {
  DATE_PARAM,
  DEFAULT_RANGE,
  appointmentTypesOf,
  dayHeading,
  filterAppointments,
  groupByDay,
  rangeDates,
} from './utils'
import {
  Wrapper,
  Header,
  Count,
  Table,
  HeaderRow,
  DayRow,
  Empty,
} from './styles'

// How long a confirmed row stays on the list, so Undo is still in reach.
const UNDO_WINDOW = 5000

const UnconfirmedAppointments = () => {
  const { onOpenSnackbar } = useSnackbarContext()
  const today = useMemo(() => nowOnClinicTimezone(), [])
  const [range, setRange] = useState(DEFAULT_RANGE)
  const [custom, setCustom] = useState({})
  const [search, setSearch] = useState('')
  const [typeId, setTypeId] = useState('all')
  const [healthHistory, setHealthHistory] = useState('any')
  const [messaging, setMessaging] = useState(null)
  const [cancelling, setCancelling] = useState(null)
  const [justConfirmed, setJustConfirmed] = useState({})
  const timers = useRef({})

  const { from, to } = rangeDates(range, today, custom)
  const {
    appointments,
    isLoading,
    isError,
    refetch,
  } = useUnconfirmedAppointments({ from, to })
  const { setConfirmed } = useSetAppointmentConfirmed()
  const { cancelAppointment, isLoading: isCancelling } = useCancelAppointment()

  useEffect(() => {
    const pending = timers.current
    return () => Object.values(pending).forEach(clearTimeout)
  }, [])

  // A confirmed appointment leaves the endpoint's answer at once; it is kept
  // here, marked, until the undo window closes.
  const listed = useMemo(() => {
    const ids = new Set(appointments.map(a => a.id))
    return [
      ...appointments,
      ...Object.values(justConfirmed).filter(a => !ids.has(a.id)),
    ]
  }, [appointments, justConfirmed])

  const types = useMemo(() => appointmentTypesOf(listed), [listed])
  const visible = filterAppointments(listed, { search, typeId, healthHistory })
  const groups = groupByDay(visible)
  const openCount = visible.filter(a => !justConfirmed[a.id]).length

  const forget = id => {
    clearTimeout(timers.current[id])
    delete timers.current[id]
    setJustConfirmed(({ [id]: _, ...rest }) => rest)
  }

  const handleConfirm = async appointment => {
    try {
      await setConfirmed({ id: appointment.id, confirmed: true })
      setJustConfirmed(current => ({
        ...current,
        [appointment.id]: appointment,
      }))
      timers.current[appointment.id] = setTimeout(
        () => forget(appointment.id),
        UNDO_WINDOW
      )
    } catch (error) {
      onOpenSnackbar(error.message, 'error')
    }
  }

  const handleUndo = async appointment => {
    try {
      await setConfirmed({ id: appointment.id, confirmed: false })
      forget(appointment.id)
    } catch (error) {
      onOpenSnackbar(error.message, 'error')
    }
  }

  const handleCancel = async ({ reason, sendNotice }) => {
    try {
      const { notice } = await cancelAppointment({
        id: cancelling.id,
        reason,
        sendNotice,
      })
      const cancelled = `${cancelling.patient.fullName}'s appointment was cancelled`
      const sentBy = [notice?.sms && 'text', notice?.email && 'email']
        .filter(Boolean)
        .join(' and ')

      if (!sendNotice) {
        onOpenSnackbar(cancelled, 'success')
      } else if (sentBy) {
        onOpenSnackbar(
          `${cancelled} and the notice sent by ${sentBy}`,
          'success'
        )
      } else {
        // No phone or email on file, or the send failed.
        onOpenSnackbar(`${cancelled}, but no notice could be sent`, 'warning')
      }
    } catch (error) {
      onOpenSnackbar(error.message, 'error')
    }
    setCancelling(null)
  }

  return (
    <Wrapper>
      <Header>
        <div>
          <h1>Unconfirmed appointments</h1>
          <p>
            Upcoming appointments the patient hasn't confirmed yet. Confirm them
            for the patient after a call, or cancel them.
          </p>
        </div>
        <Count>
          <b>{openCount}</b> unconfirmed
        </Count>
      </Header>

      <Filters
        search={search}
        onSearch={setSearch}
        range={range}
        custom={custom}
        today={format(today, DATE_PARAM)}
        onRange={(value, dates) => {
          setRange(value)
          if (dates) setCustom(dates)
        }}
        typeId={typeId}
        types={types}
        onType={setTypeId}
        healthHistory={healthHistory}
        onHealthHistory={setHealthHistory}
      />

      <Table>
        <HeaderRow>
          <div>Patient</div>
          <div>Appointment</div>
          <div>Phone</div>
          <div>Health history</div>
          <div>Reminders sent</div>
          <div />
        </HeaderRow>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : isError && appointments.length === 0 ? (
          <Empty role="alert">
            <b>We couldn't load the unconfirmed appointments</b>
            <Button variant="outlined" onClick={() => refetch()}>
              Try again
            </Button>
          </Empty>
        ) : groups.length === 0 ? (
          <Empty>
            <b>Nothing to chase here</b>
            Every appointment in this date range is confirmed.
          </Empty>
        ) : (
          groups.map(group => (
            <React.Fragment key={group.day}>
              <DayRow>
                {dayHeading(group.day, today)}
                <span>
                  {group.appointments.filter(a => !justConfirmed[a.id]).length}{' '}
                  unconfirmed
                </span>
              </DayRow>
              {group.appointments.map(appointment => (
                <AppointmentRow
                  key={appointment.id}
                  appointment={appointment}
                  confirmed={Boolean(justConfirmed[appointment.id])}
                  onMessage={() => setMessaging(appointment)}
                  onConfirm={() => handleConfirm(appointment)}
                  onUndo={() => handleUndo(appointment)}
                  onCancel={() => setCancelling(appointment)}
                />
              ))}
            </React.Fragment>
          ))
        )}
      </Table>

      <MessagesDrawer
        appointment={messaging}
        onClose={() => setMessaging(null)}
      />

      <CancelAppointmentModal
        appointment={cancelling}
        loading={isCancelling}
        onClose={() => setCancelling(null)}
        onCancel={handleCancel}
      />
    </Wrapper>
  )
}

export default UnconfirmedAppointments
